import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

type AuditRow = {
  id: string
  actor_user_id: string
  actor_email: string
  action: string
  subject_type: string
  subject_id?: string | null
  subject_ref?: string | null
  details: Record<string, unknown>
  happened_at: string
  ip_address?: string | null
  user_agent?: string | null
}

// Subject types we currently emit. Kept as an allowlist to keep the filter tight.
const SUBJECT_TYPES = ['', 'user', 'tenant', 'source'] as const

export default function AdminAuditPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])

  const [action, setAction] = useState('')
  const [subjectType, setSubjectType] = useState<string>('')
  const [page, setPage] = useState(1)
  const pageSize = 50

  const offset = (page - 1) * pageSize

  const q = useQuery({
    queryKey: ['admin', 'audit', { action, subjectType, page }],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (action) params.set('action', action)
      if (subjectType) params.set('subject_type', subjectType)
      params.set('limit', String(pageSize))
      params.set('offset', String(offset))
      return client.callEnvelope<AuditRow[]>(`/api/v1/admin/audit?${params}`)
    },
  })

  const total = q.data?.meta?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          Audit trail
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="subj-lbl">Subject</InputLabel>
          <Select
            labelId="subj-lbl"
            value={subjectType}
            input={<OutlinedInput label="Subject" />}
            onChange={(e: SelectChangeEvent) => { setSubjectType(e.target.value); setPage(1) }}
          >
            {SUBJECT_TYPES.map((s) => (
              <MenuItem key={s} value={s}>{s === '' ? 'All' : s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="action-lbl">Action</InputLabel>
          <Select
            labelId="action-lbl"
            value={action}
            input={<OutlinedInput label="Action" />}
            onChange={(e: SelectChangeEvent) => { setAction(e.target.value); setPage(1) }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="user.created">user.created</MenuItem>
            <MenuItem value="user.updated">user.updated</MenuItem>
            <MenuItem value="user.deactivated">user.deactivated</MenuItem>
            <MenuItem value="password.reset_requested">password.reset_requested</MenuItem>
            <MenuItem value="password.reset_confirmed">password.reset_confirmed</MenuItem>
            <MenuItem value="password.admin_reset_issued">password.admin_reset_issued</MenuItem>
            <MenuItem value="password.changed">password.changed</MenuItem>
            <MenuItem value="tenant.updated">tenant.updated</MenuItem>
            <MenuItem value="source.created">source.created</MenuItem>
            <MenuItem value="source.updated">source.updated</MenuItem>
            <MenuItem value="source.deleted">source.deleted</MenuItem>
            <MenuItem value="source.secret_rotated">source.secret_rotated</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {q.isLoading && <Typography variant="body2">Loading…</Typography>}
      {q.isError && (
        <Alert severity="error">{q.error instanceof Error ? q.error.message : 'Failed to load audit.'}</Alert>
      )}

      {q.data && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {q.data.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                      No audit rows match those filters.
                    </TableCell>
                  </TableRow>
                )}
                {q.data.data?.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2">{new Date(r.happened_at).toLocaleString('en-GB')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.actor_email || r.actor_user_id.slice(0, 8)}</Typography>
                      {r.ip_address && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.ip_address}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={r.action} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        <b>{r.subject_type}</b>
                        {r.subject_ref ? ` · ${r.subject_ref}` : ''}
                      </Typography>
                      {r.subject_id && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                          {r.subject_id.slice(0, 8)}…
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          fontSize: '0.75rem',
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxWidth: 320,
                          maxHeight: 100,
                          overflow: 'auto',
                        }}
                      >
                        {JSON.stringify(r.details, null, 2)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
              {total > 0
                ? `Showing ${offset + 1}–${offset + (q.data.data?.length ?? 0)} of ${total}`
                : '0 rows'}
            </Typography>
            <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} size="small" />
          </Stack>
        </>
      )}
    </Paper>
  )
}
